import { AlertTriangle, Info, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { attemptsLeft, type AttemptBudget } from "@/lib/validationSubmission";

const DEFAULT_DESCRIPTION =
  "Nutze das Feedback und überarbeite deine Antwort – sie bleibt erhalten.";

const INFO_SURFACE = "border-info-border bg-info-light text-info-foreground";

export interface QuizAttemptHintProps extends AttemptBudget {
  /** Replaces the retry copy, e.g. to speak of gaps instead of one answer. */
  description?: string;
  className?: string;
}

type Tone = {
  className: string;
  icon: typeof RotateCcw;
  headline: string;
};

/**
 * Escalating tone: a plain retry stays informational, the final try warns, and
 * a spent budget is a calm n/m count rather than a second fail.
 */
const toneFor = (left: number, maxAttempts: number): Tone => {
  if (left === 0) {
    return {
      className: INFO_SURFACE,
      icon: Info,
      headline: `${maxAttempts}/${maxAttempts} Versuche`,
    };
  }
  if (left === 1) {
    return {
      className:
        "border-warning-border bg-warning-light text-warning-foreground",
      icon: AlertTriangle,
      headline: "Letzter Versuch",
    };
  }
  return {
    className: INFO_SURFACE,
    icon: RotateCcw,
    headline: `Noch ${left} Versuche übrig`,
  };
};

/**
 * Banner announcing how much of the attempt budget is left, sat above the
 * verdict so the learner reads "you get another go" before the error itself.
 *
 * Renders nothing until the server has reported a budget: uncapped types have
 * nothing to count down, and before the first verdict the numbers aren't known
 * yet — the cap lives on the backend, not in the storyboard.
 */
export const QuizAttemptHint = ({
  attemptCount,
  maxAttempts,
  description = DEFAULT_DESCRIPTION,
  className,
}: QuizAttemptHintProps) => {
  const left = attemptsLeft({ attemptCount, maxAttempts });
  if (left === null || attemptCount == null || maxAttempts == null) return null;

  const tone = toneFor(left, maxAttempts);
  const Icon = tone.icon;

  return (
    <div
      role="status"
      className={cn(
        "flex w-full self-stretch items-start gap-3 rounded-lg border border-l-4 px-4 py-3",
        tone.className,
        className
      )}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-bold">{tone.headline}</p>
        {left > 0 && <p className="mt-1">{description}</p>}
      </div>
    </div>
  );
};

export default QuizAttemptHint;
